import {ReactElement, ReactNode, useMemo, useState} from "react";
import {IconButton} from "@zendeskgarden/react-buttons";
import {Checkbox, Field, Label, MediaInput} from "@zendeskgarden/react-forms";
import {TooltipModal} from "@zendeskgarden/react-modals";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import {Inline} from "@zendeskgarden/react-loaders";
import {ReactComponent as GearIcon} from "@zendeskgarden/svg-icons/src/16/gear-stroke.svg";
import {ReactComponent as SearchIcon} from "@zendeskgarden/svg-icons/src/16/search-stroke.svg";
import {
    PayrollBonusFrequency,
    PayrollBonusType,
    PayrollCompensationItemType,
    PayrollDefaultsForPositionQuery,
    PayrollRaiseFrequency,
    PayrollRaiseType,
    usePayrollDefaultsForPositionQuery,
} from "../../../../__generated__/generated_types";
import {useProperties, Property} from "../../../../contexts/properties/PropertiesContext";
import {DateFormatter} from "../../../../components/analyst-payroll-drawer/helpers/utils";
import {ensureDateInLocale} from "../../../../utils/helper-funcs";
import {acquisitionPropertyDateDisplayString} from "../../../workflows/account/formula-bar/logic/utils";
import styles from "./PositionDefaultsByProperty.module.scss";

interface PositionDefaultsByPropertyProps {
    positionIds: string[];
}

type PayrollDefaultsForPosition = PayrollDefaultsForPositionQuery["payrollDefaultsForPosition"][0];
type PayrollDefaultRaise = PayrollDefaultsForPosition["raises"][0];
type PayrollDefaultBonus = PayrollDefaultsForPosition["bonuses"][0];
type PayrollDefaultOvertime = PayrollDefaultsForPosition["overtime"][0];
type PayrollDefaultCompItem = PayrollDefaultsForPosition["compItems"][0];
type SectionId =
    | "baseComp"
    | "raises"
    | "bonuses"
    | "overtime"
    | "medical"
    | "dental"
    | "vision"
    | "r401k"
    | "medicare"
    | "socialSecurity"
    | "futa"
    | "suta"
    | "wc";

interface PositionDefaultsTableRow {
    propertyId: string;
    propertyName: string;
    property: Property | undefined;
    rowNum: number;
    baseComp: PayrollDefaultsForPosition["baseComp"] | undefined;
    raise: PayrollDefaultRaise | undefined;
    bonus: PayrollDefaultBonus | undefined;
    overtime: PayrollDefaultOvertime | undefined;
    medical: PayrollDefaultCompItem | undefined;
    dental: PayrollDefaultCompItem | undefined;
    vision: PayrollDefaultCompItem | undefined;
    r401k: PayrollDefaultCompItem | undefined;
    medicare: PayrollDefaultCompItem | undefined;
    socialSecurity: PayrollDefaultCompItem | undefined;
    futa: PayrollDefaultCompItem | undefined;
    suta: PayrollDefaultCompItem | undefined;
    wc: PayrollDefaultCompItem | undefined;
}

interface SectionColumn {
    id: string;
    label: string;
    className: string;
}

interface SectionConfig {
    id: SectionId;
    label: string;
    groupClassName: string;
    columns: SectionColumn[];
    renderValues: (row: PositionDefaultsTableRow) => ReactNode[];
}

const SECTION_ORDER: SectionId[] = [
    "baseComp",
    "raises",
    "bonuses",
    "overtime",
    "medical",
    "dental",
    "vision",
    "r401k",
    "medicare",
    "socialSecurity",
    "futa",
    "suta",
    "wc",
];

export default function PositionDefaultsByProperty(props: PositionDefaultsByPropertyProps): ReactElement {
    const {positionIds} = props;
    const positionId = positionIds[0] ?? "";
    const {properties} = useProperties();
    const propertyById = useMemo(() => (properties ?? []).toIdMap("id"), [properties]);
    const [filterRefElement, setFilterRefElement] = useState<HTMLElement | null>(null);
    const [visibleSectionIds, setVisibleSectionIds] = useState<SectionId[]>(SECTION_ORDER);
    const [propertySearch, setPropertySearch] = useState("");

    const {data, loading, error} = usePayrollDefaultsForPositionQuery({
        variables: {positionId},
        fetchPolicy: "no-cache",
        skip: positionIds.length !== 1 || !positionId,
    });

    const rows = useMemo(() => {
        return buildRows(data?.payrollDefaultsForPosition ?? [], propertyById);
    }, [data?.payrollDefaultsForPosition, propertyById]);
    const filteredRows = useMemo(() => {
        const search = propertySearch.trim().toLowerCase();
        if(!search) {
            return rows;
        }

        return rows.filter(row => row.propertyName.toLowerCase().includes(search));
    }, [propertySearch, rows]);
    const sectionConfigs = useMemo(() => buildSectionConfigs(), []);
    const visibleSections = useMemo(() => {
        const visibleIds = new Set(visibleSectionIds);
        return sectionConfigs.filter(section => visibleIds.has(section.id));
    }, [sectionConfigs, visibleSectionIds]);
    const visibleColumnCount = 1 + visibleSections.reduce((sum, section) => sum + section.columns.length, 0);

    if(positionIds.length === 0) {
        return <div className={styles.selectorMessage}>Select a position to view position defaults.</div>;
    }

    if(positionIds.length > 1) {
        return <div className={styles.selectorMessage}>Select only one position to view position defaults.</div>;
    }

    if(loading) {
        return <Inline />;
    }

    if(error) {
        return <div className={styles.selectorMessage}>Unable to load position defaults.</div>;
    }

    return (
        <>
            <div className={styles.positionDefaultsControls}>
                <Field className={styles.positionDefaultsSearchField}>
                    <Label hidden>Search properties</Label>
                    <MediaInput
                        value={propertySearch}
                        onChange={event => setPropertySearch(event.target.value)}
                        placeholder="Search property"
                        start={<SearchIcon />}
                    />
                </Field>
                <IconButton aria-label="Select visible sections" onClick={event => setFilterRefElement(event.currentTarget as HTMLElement)}>
                    <GearIcon />
                </IconButton>
            </div>
            <TooltipModal
                referenceElement={filterRefElement}
                onClose={() => setFilterRefElement(null)}
                hasArrow={false}
            >
                <TooltipModal.Body className={styles.positionDefaultsTooltipBody}>
                    {sectionConfigs.map(section => (
                        <Field key={section.id} className={styles.positionDefaultsCheckboxField}>
                            <Checkbox
                                checked={visibleSectionIds.includes(section.id)}
                                onChange={event => {
                                    setVisibleSectionIds(current =>
                                        event.target.checked ?
                                            [...current, section.id].filter((id, index, ids) => ids.indexOf(id) === index) :
                                            current.filter(id => id !== section.id)
                                    );
                                }}
                            >
                                <Label>{section.label}</Label>
                            </Checkbox>
                        </Field>
                    ))}
                </TooltipModal.Body>
            </TooltipModal>
            <div className={styles.positionDefaultsTableWrapper}>
                <Table className={styles.positionDefaultsTable} style={{width: "auto"}}>
                <Head>
                    <HeaderRow className={styles.positionDefaultsGroupHeaderRow}>
                        <HeaderCell rowSpan={visibleSections.length > 0 ? 2 : 1} className={styles.propertyDefaultsPropertyCell}>Property</HeaderCell>
                        {visibleSections.map(section => (
                            <HeaderCell key={section.id} colSpan={section.columns.length} className={section.groupClassName}>
                                {section.label.toUpperCase()}
                            </HeaderCell>
                        ))}
                    </HeaderRow>
                    {visibleSections.length > 0 && (
                        <HeaderRow className={styles.positionDefaultsHeaderRow}>
                            {visibleSections.flatMap(section => section.columns.map(column => (
                                <HeaderCell key={`${section.id}-${column.id}`} className={column.className}>
                                    {column.label}
                                </HeaderCell>
                            )))}
                        </HeaderRow>
                    )}
                </Head>
                <Body>
                    {filteredRows.length === 0 ? (
                        <Row>
                            <Cell colSpan={visibleColumnCount}>{propertySearch.trim() ? "No matching properties found." : "No defaults found."}</Cell>
                        </Row>
                    ) : filteredRows.map(row => (
                        <Row key={`${row.propertyId}-${row.rowNum}`} className={styles.positionDefaultsBodyRow}>
                            <Cell className={styles.propertyDefaultsPropertyCell}>{row.rowNum === 0 ? row.propertyName : ""}</Cell>
                            {visibleSections.flatMap(section => renderSectionCells(section, row))}
                        </Row>
                    ))}
                </Body>
                </Table>
            </div>
        </>
    );
}

function buildSectionConfigs(): SectionConfig[] {
    return [
        {
            id: "baseComp",
            label: "Base Compensation",
            groupClassName: styles.baseCompHeader,
            columns: [
                {id: "type", label: "Type", className: styles.valueCell},
                {id: "annual", label: "Annualized Amount", className: styles.valueCell},
                {id: "hourly", label: "Hourly Rate", className: styles.valueCell},
                {id: "hours", label: "Hours/Week", className: styles.valueCell},
            ],
            renderValues: row => [
                row.baseComp?.compensationType ?? "",
                formatCurrency(row.baseComp?.annualCompensation),
                formatCurrency(row.baseComp?.hourlyCompensation),
                row.baseComp?.expectedHoursPerWeek ?? "",
            ],
        },
        {
            id: "raises",
            label: "Raises",
            groupClassName: styles.raisesHeader,
            columns: [
                {id: "number", label: "#", className: styles.valueCell},
                {id: "type", label: "Type", className: styles.valueCell},
                {id: "percent", label: "Percent (%)", className: styles.valueCell},
                {id: "amount", label: "Amount ($)", className: styles.valueCell},
                {id: "date", label: "Effective Date", className: styles.dateCell},
            ],
            renderValues: row => [
                row.raise ? row.rowNum : "",
                row.raise?.raiseType ?? "",
                row.raise?.raiseType === PayrollRaiseType.PercentOfSalary ? row.raise.amount : "",
                row.raise?.raiseType === PayrollRaiseType.FixedAmount ? row.raise.amount : "",
                formatRaiseDate(row.raise, row.property),
            ],
        },
        {
            id: "bonuses",
            label: "Bonuses",
            groupClassName: styles.bonusesHeader,
            columns: [
                {id: "number", label: "#", className: styles.valueCell},
                {id: "type", label: "Type", className: styles.valueCell},
                {id: "percent", label: "Percent (%)", className: styles.valueCell},
                {id: "amount", label: "Amount ($)", className: styles.valueCell},
                {id: "date", label: "Bonus Date", className: styles.dateCell},
            ],
            renderValues: row => [
                row.bonus ? row.rowNum : "",
                row.bonus?.bonusType ?? "",
                row.bonus?.bonusType === PayrollBonusType.PercentOfSalary ? row.bonus.amount : "",
                row.bonus?.bonusType === PayrollBonusType.FixedAmount ? row.bonus.amount : "",
                formatBonusDate(row.bonus, row.property),
            ],
        },
        {
            id: "overtime",
            label: "Overtime",
            groupClassName: styles.overtimeHeader,
            columns: [
                {id: "number", label: "#", className: styles.valueCell},
                {id: "hourly", label: "Hourly Rate", className: styles.valueCell},
                {id: "hours", label: "Hours/Week", className: styles.valueCell},
                {id: "start", label: "Start Date", className: styles.dateCell},
                {id: "end", label: "End Date", className: styles.dateCell},
            ],
            renderValues: row => [
                row.overtime ? row.rowNum : "",
                row.overtime?.hourlyCompensation ?? "",
                row.overtime?.expectedHoursPerWeek ?? "",
                formatDate(row.overtime?.effectiveStartDate, row.property),
                formatDate(row.overtime?.effectiveEndDate, row.property),
            ],
        },
        buildBenefitSection("medical", "Medical", row => row.medical),
        buildBenefitSection("dental", "Dental", row => row.dental),
        buildBenefitSection("vision", "Vision", row => row.vision),
        buildTaxAndInsuranceSection("r401k", "401K", row => row.r401k),
        buildTaxAndInsuranceSection("medicare", "Medicare", row => row.medicare),
        buildTaxAndInsuranceSection("socialSecurity", "Social Security Tax", row => row.socialSecurity),
        buildTaxAndInsuranceSection("futa", "Federal Unemployment Tax", row => row.futa),
        buildTaxAndInsuranceSection("suta", "State Unemployment Tax", row => row.suta),
        buildTaxAndInsuranceSection("wc", "Workers Compensation", row => row.wc),
    ];
}

function buildBenefitSection(id: SectionId, label: string, getItem: (row: PositionDefaultsTableRow) => PayrollDefaultCompItem | undefined): SectionConfig {
    return {
        id,
        label,
        groupClassName: styles.benefitHeader,
        columns: [
            {id: "number", label: "#", className: styles.valueCell},
            {id: "cost", label: "Cost per Pay Period", className: styles.valueCell},
            {id: "date", label: "Effective Date", className: styles.dateCell},
        ],
        renderValues: row => {
            const item = getItem(row);
            return [
                item ? row.rowNum : "",
                item?.costPerPayPeriod ?? "",
                formatDate(item?.effectiveStartDate, row.property),
            ];
        },
    };
}

function buildTaxAndInsuranceSection(id: SectionId, label: string, getItem: (row: PositionDefaultsTableRow) => PayrollDefaultCompItem | undefined): SectionConfig {
    return {
        id,
        label,
        groupClassName: styles.taxAndInsuranceHeader,
        columns: [
            {id: "number", label: "#", className: styles.valueCell},
            {id: "percent", label: "% of Salary", className: styles.valueCell},
            {id: "cap", label: "Contribution Cap", className: styles.valueCell},
            {id: "date", label: "Effective Date", className: styles.dateCell},
        ],
        renderValues: row => {
            const item = getItem(row);
            return [
                item ? row.rowNum : "",
                item?.costPerPayPeriod ?? "",
                item?.annualCostCap ?? "",
                formatDate(item?.effectiveStartDate, row.property),
            ];
        },
    };
}

function renderSectionCells(section: SectionConfig, row: PositionDefaultsTableRow): ReactElement[] {
    return section.renderValues(row).map((value, index) => (
        <Cell key={`${section.id}-${section.columns[index]?.id ?? index}`} className={section.columns[index]?.className}>
            {value}
        </Cell>
    ));
}

function buildRows(
    defaults: PayrollDefaultsForPosition[],
    propertyById: Record<string, Property>
): PositionDefaultsTableRow[] {
    const filteredDefault: {defaultsForProperty: PayrollDefaultsForPosition, property: Property}[] = [];
    for(const defaultsForProperty of defaults) {
        const property = propertyById[defaultsForProperty.propertyId];
        if(!property) {
            continue;
        }
        filteredDefault.push({defaultsForProperty, property});
    }
    return filteredDefault.sortBy(row => row.property.name)
        .flatMap(({defaultsForProperty, property}) => {
            const medical = compItemsForType(defaultsForProperty.compItems, PayrollCompensationItemType.Medical);
            const dental = compItemsForType(defaultsForProperty.compItems, PayrollCompensationItemType.Dental);
            const vision = compItemsForType(defaultsForProperty.compItems, PayrollCompensationItemType.Vision);
            const r401k = compItemsForType(defaultsForProperty.compItems, PayrollCompensationItemType.Retirement_401KMatch);
            const medicare = compItemsForType(defaultsForProperty.compItems, PayrollCompensationItemType.MedicareTax);
            const socialSecurity = compItemsForType(defaultsForProperty.compItems, PayrollCompensationItemType.SocialSecurityTax);
            const futa = compItemsForType(defaultsForProperty.compItems, PayrollCompensationItemType.FederalUnemploymentTax);
            const suta = compItemsForType(defaultsForProperty.compItems, PayrollCompensationItemType.StateUnemploymentTax);
            const wc = compItemsForType(defaultsForProperty.compItems, PayrollCompensationItemType.WorkersCompensation);

            const maxRows = Math.max(
                1,
                defaultsForProperty.raises.length,
                defaultsForProperty.bonuses.length,
                defaultsForProperty.overtime.length,
                medical.length,
                dental.length,
                vision.length,
                r401k.length,
                medicare.length,
                socialSecurity.length,
                futa.length,
                suta.length,
                wc.length
            );

            return new Array(maxRows).fill(null).map((_, index) => ({
                propertyId: defaultsForProperty.propertyId,
                propertyName: property.name,
                property,
                rowNum: index,
                baseComp: index === 0 ? defaultsForProperty.baseComp ?? undefined : undefined,
                raise: defaultsForProperty.raises[index],
                bonus: defaultsForProperty.bonuses[index],
                overtime: defaultsForProperty.overtime[index],
                medical: medical[index],
                dental: dental[index],
                vision: vision[index],
                r401k: r401k[index],
                medicare: medicare[index],
                socialSecurity: socialSecurity[index],
                futa: futa[index],
                suta: suta[index],
                wc: wc[index],
            }));
        });
}

function compItemsForType(items: PayrollDefaultCompItem[], type: PayrollCompensationItemType): PayrollDefaultCompItem[] {
    return items.filter(item => item.compensationItem.type === type);
}

function formatCurrency(value: string | number | null | undefined): string {
    return value ? `$${value}` : "";
}

function formatDate(date: string | null | undefined, property: Property | undefined): string {
    if(!date) {
        return "";
    }

    if(property?.isAcquisitionProperty) {
        return acquisitionPropertyDateDisplayString(date, property);
    }

    return DateFormatter.format(ensureDateInLocale(date));
}

function formatRaiseDate(raise: PayrollDefaultRaise | undefined, property: Property | undefined): string {
    if(!raise) {
        return "";
    }

    if(raise.frequency === PayrollRaiseFrequency.Once) {
        return formatDate(raise.onetimeRaiseDate, property);
    }

    if(raise.frequency === PayrollRaiseFrequency.Annually && raise.raiseDay !== null && raise.raiseDay !== undefined && raise.raiseMonth !== null && raise.raiseMonth !== undefined) {
        return formatMonthDay(raise.raiseMonth, raise.raiseDay);
    }

    return "";
}

function formatBonusDate(bonus: PayrollDefaultBonus | undefined, property: Property | undefined): string {
    if(!bonus) {
        return "";
    }

    if(bonus.frequency === PayrollBonusFrequency.Once) {
        return formatDate(bonus.onetimeBonusDate, property);
    }

    if(bonus.frequency === PayrollBonusFrequency.Annually && bonus.bonusDay !== null && bonus.bonusDay !== undefined && bonus.bonusMonth !== null && bonus.bonusMonth !== undefined) {
        return formatMonthDay(bonus.bonusMonth, bonus.bonusDay);
    }

    return "";
}

function formatMonthDay(month: number, day: number): string {
    return `0${month + 1}`.slice(-2) + "/" + `0${day}`.slice(-2);
}
